import {
  CollisionDetection,
  DndContext,
  DragCancelEvent,
  DragEndEvent,
  DragMoveEvent,
  DragOverlay,
  DragStartEvent,
  MouseSensor,
  UniqueIdentifier,
  closestCorners,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { useSortable } from '@dnd-kit/sortable';
import BlockIcon from '@mui/icons-material/Block';
import { useAtomValue } from 'jotai';
import {
  CSSProperties,
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type JSX,
} from 'react';
import { styled } from '@mui/material/styles';
import { CSS } from '@dnd-kit/utilities';

import { SelectItem, ViewItem } from '../../../../../types';
import { selectedViewPFJ } from '../../../../../pages/PortfolioOverview/state/ViewStateJ';
import { useViewNavigation } from '../../../../../pages/PortfolioOverview/components/ViewNavigation/ViewNavigationProvider';
import { VIEW_TYPE, ViewTypeByData } from '../../../../../data-models/view-config.data-model';
import { ListContainer } from '../../../../SortableMultiList/ListContainer';
import { useMultiSortableList } from '../../../../SortableMultiList/useMultiSortableList';
import { IconTypes } from '../../../../Icon';
import { ViewOption } from './ViewOption';

type SectionId = 'default' | 'custom';

interface Props {
  items: ViewItem[];
  onItemSelect: (item: SelectItem) => void;
  onReorder?: (reorderedItems: ViewItem[]) => void;
  style?: CSSProperties;
}

interface ItemProps {
  item: ViewItem;
  onClick: (item: SelectItem) => void;
  isSelected: boolean;
  viewTypeIcon?: string;
  viewType: VIEW_TYPE;
  suppressMenu?: boolean;
}

const OptionsWrapper = styled('div')`
  max-height: 8rem;
  overflow-y: auto;
  overflow-x: hidden;
  overscroll-behavior: contain;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const OptionWrapper = styled('div')``;

const listContainerStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
};

// Placeholder divider between the default and custom view groups. Final visual
// treatment is pending design (FD-3357).
const SectionDelimiter = styled('div')`
  height: 1px;
  margin: 2px 0;
  background: rgba(255, 255, 255, 0.12);
`;

// The row that follows the cursor in the DragOverlay. Rendering the dragged row
// in an overlay (instead of transforming it in place) is what keeps the list
// from jittering as the pointer crosses the section boundary.
const OverlayRow = styled('div')<{ blocked: boolean }>`
  position: relative;
  border-radius: 4px;
  background: #2d3277;
  cursor: ${({ blocked }) => (blocked ? 'not-allowed' : 'grabbing')};
  opacity: ${({ blocked }) => (blocked ? 0.6 : 0.95)};
`;

const BlockBadge = styled('div')`
  position: absolute;
  top: 50%;
  right: 8px;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  color: #ff6b6b;
  pointer-events: none;
`;

const DraggableItem = ({
  item,
  onClick,
  isSelected,
  viewTypeIcon,
  viewType,
  suppressMenu,
}: ItemProps): JSX.Element => {
  const { setNodeRef, transform, transition, attributes, listeners, isDragging } = useSortable({
    id: item.id as string,
  });

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    // Dim the source row while its clone rides in the DragOverlay.
    opacity: isDragging ? 0.4 : 1,
  };
  return (
    <OptionWrapper ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <ViewOption
        viewType={viewType}
        viewTypeIcon={viewTypeIcon}
        isSelected={isSelected}
        isDraggable
        text={item.value}
        onClick={() => onClick(item)}
        suppressMenu={suppressMenu}
      />
    </OptionWrapper>
  );
};

export const Options = ({ items, onItemSelect, onReorder, style }: Props): ReactElement => {
  const { viewsMap } = useViewNavigation();
  const selectedView = useAtomValue(selectedViewPFJ);

  const getViewTypeIcon = useCallback(
    (id: string | number) => {
      const viewType = viewsMap[id]?.type;
      if (viewType === VIEW_TYPE.COMPANY) {
        return undefined;
      }
      if (viewType === VIEW_TYPE.PERSONAL && !!viewsMap[id]?.sharing?.length) {
        return IconTypes.USER_SHARED_BY_ME;
      }
      if (viewType === VIEW_TYPE.PERSONAL) {
        return IconTypes.USER_HEART;
      }
      if (viewType === VIEW_TYPE.SHARED) {
        return IconTypes.USER_SHARED_WITH_ME;
      }
    },
    [viewsMap]
  );

  const viewType = useCallback(
    (id: string | number) => {
      return viewsMap[id]?.type;
    },
    [viewsMap]
  );

  // Two isolated groups: default (company) views and custom (personal/shared)
  // views. Reordering is confined to a single group, satisfying FD-3357's
  // within-section constraint.
  const groupedItems = useMemo(
    () => ({
      default: items.filter((item) => item.isCompany),
      custom: items.filter((item) => !item.isCompany),
    }),
    [items]
  );

  const { handleDragStart, handleDragEnd } = useMultiSortableList<ViewItem>({
    items: groupedItems,
    onChange: (grouped) => {
      onReorder?.([...grouped.default, ...grouped.custom]);
    },
  });

  // The row currently being dragged (rendered in the DragOverlay), and whether
  // the pointer is hovering the section the item may NOT be dropped into.
  const [activeItem, setActiveItem] = useState<ViewItem | null>(null);
  const [dropBlocked, setDropBlocked] = useState(false);

  const defaultSectionRef = useRef<HTMLDivElement | null>(null);
  const customSectionRef = useRef<HTMLDivElement | null>(null);

  const sectionOf = useCallback(
    (id?: UniqueIdentifier | null): SectionId | undefined => {
      if (id == null) {
        return undefined;
      }
      if (id === 'default' || id === 'custom') {
        return id;
      }
      if (groupedItems.default.some((item) => item.id === id)) {
        return 'default';
      }
      if (groupedItems.custom.some((item) => item.id === id)) {
        return 'custom';
      }
      return undefined;
    },
    [groupedItems]
  );

  // Which section the pointer physically sits over, by hit-testing the two
  // group wrappers. Used to drive the not-allowed affordance independently of
  // the (section-confined) collision detection.
  const sectionAtPoint = useCallback((x: number, y: number): SectionId | undefined => {
    const contains = (el: HTMLDivElement | null): boolean => {
      if (!el) {
        return false;
      }
      const r = el.getBoundingClientRect();
      return x >= r.left && x <= r.right && y >= r.top && y <= r.bottom;
    };
    if (contains(defaultSectionRef.current)) {
      return 'default';
    }
    if (contains(customSectionRef.current)) {
      return 'custom';
    }
    return undefined;
  }, []);

  // Confine collisions to the dragged item's own section so the other list
  // never reshuffles (no jitter) and offers no drop target. While the pointer
  // is over the forbidden section we surface no target at all, freezing the
  // sort preview until the user returns to the valid section.
  const collisionDetection = useCallback<CollisionDetection>(
    (args) => {
      if (dropBlocked) {
        return [];
      }
      const activeSection = sectionOf(args.active.id);
      const sameSection = args.droppableContainers.filter((c) => sectionOf(c.id) === activeSection);
      return closestCorners({ ...args, droppableContainers: sameSection });
    },
    [dropBlocked, sectionOf]
  );

  // Keep the existing distance constraint so a click still selects a view
  // rather than starting a drag.
  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 5,
      },
    })
  );

  const onDragStart = useCallback(
    (event: DragStartEvent) => {
      handleDragStart(event);
      setActiveItem(items.find((item) => item.id === event.active.id) ?? null);
      setDropBlocked(false);
    },
    [handleDragStart, items]
  );

  const onDragMove = useCallback(
    (event: DragMoveEvent) => {
      const activator = event.activatorEvent;
      if (!(activator instanceof MouseEvent)) {
        return;
      }
      const pointerSection = sectionAtPoint(
        activator.clientX + event.delta.x,
        activator.clientY + event.delta.y
      );
      const activeSection = sectionOf(event.active.id);
      const blocked = !!pointerSection && !!activeSection && pointerSection !== activeSection;
      setDropBlocked((prev) => (prev === blocked ? prev : blocked));
    },
    [sectionAtPoint, sectionOf]
  );

  const onDragEnd = useCallback(
    (event: DragEndEvent) => {
      // A drop over the forbidden section is rejected: the overlay animates
      // back and no reorder is persisted.
      if (!dropBlocked) {
        handleDragEnd(event);
      }
      setActiveItem(null);
      setDropBlocked(false);
    },
    [dropBlocked, handleDragEnd]
  );

  const onDragCancel = useCallback((_event: DragCancelEvent) => {
    setActiveItem(null);
    setDropBlocked(false);
  }, []);

  // Reflect the drop state in the cursor while a drag is in progress.
  useEffect(() => {
    document.body.style.cursor = activeItem ? (dropBlocked ? 'not-allowed' : 'grabbing') : '';
    return () => {
      document.body.style.cursor = '';
    };
  }, [activeItem, dropBlocked]);

  const renderItem = useCallback(
    (item: ViewItem) => (
      <DraggableItem
        item={item}
        onClick={onItemSelect}
        isSelected={selectedView?.viewId === item.id}
        viewTypeIcon={getViewTypeIcon(item.id)}
        viewType={viewType(item.id)}
        // hide until we have a way to save this view type
        // FIXME: https://foresightdata.atlassian.net/browse/MAGGIE-5710
        suppressMenu={item.isCompany && item.viewType === ViewTypeByData.people}
      />
    ),
    [getViewTypeIcon, onItemSelect, selectedView?.viewId, viewType]
  );

  const hasBothGroups = groupedItems.default.length > 0 && groupedItems.custom.length > 0;

  return (
    <OptionsWrapper style={style}>
      <DndContext
        sensors={sensors}
        collisionDetection={collisionDetection}
        onDragStart={onDragStart}
        onDragMove={onDragMove}
        onDragEnd={onDragEnd}
        onDragCancel={onDragCancel}
      >
        <div ref={defaultSectionRef}>
          <ListContainer
            id='default'
            items={groupedItems.default}
            renderItem={renderItem}
            containerStyle={listContainerStyle}
          />
        </div>
        {hasBothGroups && <SectionDelimiter />}
        <div ref={customSectionRef}>
          <ListContainer
            id='custom'
            items={groupedItems.custom}
            renderItem={renderItem}
            containerStyle={listContainerStyle}
          />
        </div>
        <DragOverlay>
          {activeItem ? (
            <OverlayRow blocked={dropBlocked}>
              <ViewOption
                viewType={viewType(activeItem.id)}
                viewTypeIcon={getViewTypeIcon(activeItem.id)}
                isSelected={selectedView?.viewId === activeItem.id}
                isDraggable
                text={activeItem.value}
                onClick={() => undefined}
                suppressMenu
              />
              {dropBlocked && (
                <BlockBadge>
                  <BlockIcon fontSize='small' />
                </BlockBadge>
              )}
            </OverlayRow>
          ) : null}
        </DragOverlay>
      </DndContext>
    </OptionsWrapper>
  );
};
